import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  MDFeOutput,
  tipoEmitenteMDFeOptions,
  tipoTransportadorMDFeOptions,
  tipoProprietarioMDFeOptions,
  modalMDFeOptions,
  tipoRodadoMDFeOptions,
  tipoCarroceriaMDFeOptions,
  tipoCargaMDFeOptions,
  tipoComponentePagamentoMDFeOptions,
  tipoValePedagioMDFeOptions,
  indicadorPagamentoBancarioMDFeOptions,
  obterDescricaoCategoriaCombinacaoVeicular,
  obterNomeBancoCompe,
  getDescricaoStatusEnvioMDFe,
  getVisualStatusMDFe,
} from "../MDFe.Dto";
import {
  MDFeBuscarXML,
  MDFeFiltrar,
  MDFeImprimirDAMDFE,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import {
  FormatarCEP,
  FormatarCPF,
  FormatarCNPJ,
  FormatarDataHoraDefault,
  MapearErrosServidor,
} from "src/hook/Functions";

const MDFeVisualizaCode = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [mdfe, setMdfe] = useState<MDFeOutput | undefined>(location.state?.mdfe);
  const [loading, setLoading] = useState(!mdfe && !!id);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!mdfe && id) {
      loadMDFe(Number(id));
    }
  }, [id, mdfe]);

  const loadMDFe = async (mdfeId: number) => {
    setLoading(true);
    try {
      const response = await MDFeFiltrar({ id: mdfeId });
      if (response.data.success && response.data.data.length > 0) {
        setMdfe(response.data.data[0]);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do MDF-e:", error);
      showToast({ message: "Erro ao carregar dados do MDF-e", variant: "Danger" });
    } finally {
      setLoading(false);
    }
  };

  const onVoltar = () => {
    navigate("/fiscal/mdfe");
  };

  const onBaixarXml = async () => {
    if (!mdfe?.id) return;
    setDownloadingXml(true);
    try {
      const nomeArquivo = `MDFe_${mdfe.numeroMDFe}`;
      const response = await MDFeBuscarXML([mdfe.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar XML do MDF-e:", error);
      showToast({ message: "Erro ao baixar XML", variant: "Danger" });
    } finally {
      setDownloadingXml(false);
    }
  };

  const onImprimir = async () => {
    if (!mdfe?.id) return;
    setPrinting(true);
    try {
      const response = await MDFeImprimirDAMDFE(mdfe.id);
      if (response.data instanceof Blob) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const isMobile = /Android|iPhone/i.test(navigator.userAgent);

        if (isMobile) {
          window.open(pdfUrl, "_blank");
        } else {
          const iframe = document.createElement("iframe");
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.src = pdfUrl;
          document.body.appendChild(iframe);

          iframe.onload = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          };
        }
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao imprimir DAMDFE:", error);
      showToast({ message: "Erro ao imprimir DAMDFE", variant: "Danger" });
    } finally {
      setPrinting(false);
    }
  };

  const formatarDoc = (val?: string) => {
    if (!val) return "-";
    const limpo = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const temLetras = /[A-Z]/.test(limpo);
    if (!temLetras && limpo.length <= 11) return FormatarCPF(limpo);
    return FormatarCNPJ(limpo);
  };

  const getTipoEmitenteLabel = (val?: number) => {
    const found = tipoEmitenteMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoTransportadorLabel = (val?: number) => {
    const found = tipoTransportadorMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getModalLabel = (val?: number) => {
    const found = modalMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoRodadoLabel = (val?: number) => {
    const found = tipoRodadoMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoCarroceriaLabel = (val?: number) => {
    const found = tipoCarroceriaMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoProprietarioLabel = (val?: number) => {
    const found = tipoProprietarioMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val !== undefined && val !== null ? String(val) : "-";
  };

  const getTipoCargaLabel = (val?: number) => {
    const found = tipoCargaMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoComponentePagamentoLabel = (val?: number) => {
    const found = tipoComponentePagamentoMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  const getTipoValePedagioLabel = (val?: string) => {
    const found = tipoValePedagioMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val || "-";
  };

  const getIndicadorPagamentoBancarioLabel = (val?: number) => {
    const found = indicadorPagamentoBancarioMDFeOptions.find((o) => o.value === String(val));
    return found ? found.label : val ? String(val) : "-";
  };

  return {
    mdfe,
    loading,
    downloadingXml,
    printing,
    onVoltar,
    onBaixarXml,
    onImprimir,
    formatarDoc,
    FormatarCEP,
    FormatarDataHoraDefault,
    getTipoEmitenteLabel,
    getTipoTransportadorLabel,
    getModalLabel,
    getTipoRodadoLabel,
    getTipoCarroceriaLabel,
    getTipoProprietarioLabel,
    getTipoCargaLabel,
    getTipoComponentePagamentoLabel,
    getTipoValePedagioLabel,
    getIndicadorPagamentoBancarioLabel,
    obterDescricaoCategoriaCombinacaoVeicular,
    obterNomeBancoCompe,
    getDescricaoStatusEnvioMDFe,
    getVisualStatusMDFe,
  };
};

export default MDFeVisualizaCode;
